<template>
  <div class="orcamento-itens">
    <div class="orcamento-itens__header">
      <div class="orcamento-itens__title-wrap">
        <div class="orcamento-itens__title">
          Itens do Orçamento
          <span class="orcamento-itens__filial ml-2"> {{ filial }}</span>
        </div>
        <div class="orcamento-itens__subtitle">
          {{ items.length }} produto(s)
        </div>
      </div>
      <div class="orcamento-itens__header-actions">
        <b-button
          v-if="exibirAcoes"
          v-on:click="$emit('add-products')"
          size="sm"
          class="orcamento-itens__add-btn"
          variant="primary"
          :disabled="!modoEdicao"
        >
          <i class="fa fa-plus"></i> Adicionar Produtos
        </b-button>
      </div>
    </div>

    <div class="orcamento-itens__body">
      <span v-if="loading" class="orcamento-itens__loading">
        <svg width="40" height="40" viewBox="0 0 50 50">
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="currentColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-dasharray="31.4 31.4"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
        Carregando itens...
      </span>

      <b-table
        v-show="!loading"
        class="w-100 orcamento-itens__table mb-0"
        :items="sortedItems"
        :fields="tableFields"
        v-on:sort-changed="onTableSortChanged"
        small
        hover
        :responsive="true"
        :sort-by="localSortBy"
        :sort-desc="localSortDesc"
        :no-local-sorting="true"
        :foot-clone="true"
        :no-footer-sorting="true"
        :no-sort-reset="true"
        stacked="sm"
        ref="cartTable"
      >
        <template #thead-top="">
          <b-tr>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('produto_codigo')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('produto_codigo')"
              @keydown.enter.prevent="toggleSort('produto_codigo')"
              @keydown.space.prevent="toggleSort('produto_codigo')"
            >
              Produto
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('quantidade')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('quantidade')"
              @keydown.enter.prevent="toggleSort('quantidade')"
              @keydown.space.prevent="toggleSort('quantidade')"
            >
              Qtde
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--custo" colspan="2">
              Custo
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--tributos" colspan="3">
              Tributos
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--margem" colspan="2">
              Margem
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--premio" colspan="2">
              Total Prêmio
            </b-th>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--venda orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('valor_total')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('valor_total')"
              @keydown.enter.prevent="toggleSort('valor_total')"
              @keydown.space.prevent="toggleSort('valor_total')"
            >
              Valor Venda
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th
              v-if="exibirAcoes"
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--solo orcamento-itens__group--acoes"
            >
              Ações
            </b-th>
          </b-tr>
        </template>

        <template v-slot:cell(produto_codigo)="data">
          <div class="orcamento-itens__produto">
            <span class="orcamento-itens__codigo">{{ data.item.produto_codigo }}</span>
            <span class="orcamento-itens__nome">{{ data.item.titulo }}</span>
            <slot name="alerta" :item="data.item"></slot>
          </div>
        </template>

        <template v-slot:cell(quantidade)="data">
          <span class="orcamento-itens__num">{{ data.item.quantidade }}</span>
        </template>

        <template v-slot:cell(valor_unitario)="data">
          <span class="orcamento-itens__money">{{ data.item.valor_unitario | money }}</span>
        </template>

        <template v-slot:cell(total_custo)="data">
          <span class="orcamento-itens__money orcamento-itens__money--custo-total ">{{ totalCusto(data.item) | money }}</span>
        </template>

        <template v-slot:cell(valor_ipi)="data">
          <span class="orcamento-itens__money">{{ ipiUnit(data.item) | money }}</span>
        </template>

        <template v-slot:cell(valor_st)="data">
          <span class="orcamento-itens__money">{{ stUnit(data.item) | money }}</span>
        </template>

        <template v-slot:cell(total_trib)="data">
          <span class="orcamento-itens__money orcamento-itens__money--trib">
            {{ totalTrib(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(margem_valor)="data">
          <span class="orcamento-itens__money">{{ data.item.margem_valor | money }}</span>
        </template>

        <template v-slot:cell(margem_total)="data">
          <span class="orcamento-itens__money orcamento-itens__money--margem">
            {{ margemTotal(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(premio_unit)="data">
          <span class="orcamento-itens__money">{{ premioUnit(data.item) | money }}</span>
        </template>

        <template v-slot:cell(valor_comissao)="data">
          <span class="orcamento-itens__money orcamento-itens__money--premio">
            {{ getComissao(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(valor_total)="data">
          <span class="orcamento-itens__money orcamento-itens__money--venda">
            {{ data.item.valor_total | money }}
          </span>
        </template>

        <template v-slot:cell(id)="data">
          <div class="orcamento-itens__acoes">
            <b-button
              class="orcamento-itens__acao orcamento-itens__acao--edit"
              size="sm"
              :disabled="!modoEdicao"
              v-on:click="$emit('edit', data.item)"
            >
              <i class="fa fa-edit"></i>
            </b-button>
            <b-button
              class="orcamento-itens__acao orcamento-itens__acao--delete"
              size="sm"
              :disabled="!modoEdicao"
              v-on:click="$emit('delete', data.item)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
        </template>

        <template v-slot:foot()>
          <span></span>
        </template>
        <template v-slot:foot(produto_codigo)>
          <span class="orcamento-itens__totais-label">Totais</span>
        </template>
        <template v-slot:foot(quantidade)>
          <span class="orcamento-itens__num">{{ qtdeTotal }}</span>
        </template>
        <template v-slot:foot(total_custo)>
          <span class="orcamento-itens__money orcamento-itens__money--custo-total" v-if="!itensAlterados">
            {{ custoTotalSum | money }}
          </span>
          <span v-else class="orcamento-itens__spinner">
            <svg width="18" height="18" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        </template>
        <template v-slot:foot(total_trib)>
          <span class="orcamento-itens__money orcamento-itens__money--trib" v-if="!itensAlterados">
            {{ tribTotalSum | money }}
          </span>
        </template>
        <template v-slot:foot(margem_total)>
          <span class="orcamento-itens__money orcamento-itens__money--margem" v-if="!itensAlterados">
            {{ margemTotalSum | money }}
          </span>
        </template>
        <template v-slot:foot(valor_comissao)>
          <span class="orcamento-itens__money orcamento-itens__money--premio" v-if="!itensAlterados">
            {{ comissaoTotalSum | money }}
          </span>
          <span v-else class="orcamento-itens__spinner">
            <svg width="18" height="18" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        </template>
        <template v-slot:foot(valor_total)>
          <span class="orcamento-itens__money orcamento-itens__money--venda" v-if="!itensAlterados">
            {{ valorTotalSum | money }}
          </span>
          <span v-else class="orcamento-itens__spinner">
            <svg width="18" height="18" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        </template>
      </b-table>
    </div>

    <div class="orcamento-itens__note">
      * ICMS ST/DIFAL, quando aplicável, será de responsabilidade do destinatário.
    </div>
  </div>
</template>

<script>
export default {
  name: "OrcamentoItensTabela",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    filial: {
      type: String,
      default: "",
    },
    modoEdicao: {
      type: Boolean,
      default: false,
    },
    exibirAcoes: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    itensAlterados: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: "produto_codigo",
    },
    sortDirection: {
      type: String,
      default: "asc",
    },
    getComissaoFn: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      localSortBy: this.sortBy,
      localSortDesc: this.sortDirection === "desc",
    };
  },
  watch: {
    sortBy(value) {
      if (value !== this.localSortBy) {
        this.localSortBy = value;
      }
    },
    sortDirection(value) {
      const desc = value === "desc";
      if (desc !== this.localSortDesc) {
        this.localSortDesc = desc;
      }
    },
  },
  computed: {
    sortedItems() {
      const key = this.localSortBy;
      if (!key) {
        return this.items;
      }

      const items = this.items.slice();
      const desc = this.localSortDesc;

      items.sort((a, b) => {
        const result = this.compareSortValues(a[key], b[key]);
        return desc ? -result : result;
      });

      return items;
    },
    tableFields() {
      const fields = [
        {
          key: "produto_codigo",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--spanned",
          tdClass: "orcamento-itens__td orcamento-itens__td--produto",
        },
        {
          key: "quantidade",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-center",
        },
        {
          key: "valor_unitario",
          label: "Vl. Unit. S/ IPI",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--custo text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "total_custo",
          label: "Total Custo",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--custo text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_ipi",
          label: "IPI Unit.",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--tributos text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_st",
          label: "ST Unit.*",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--tributos text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "total_trib",
          label: "Total Trib.",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--tributos text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "margem_valor",
          label: "Margem Unit.",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--margem text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "margem_total",
          label: "Margem Total",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--margem text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "premio_unit",
          label: "Prêmio Unit.",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--premio text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_comissao",
          label: "Total Prêmio",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--premio text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_total",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--venda orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-right",
        },
      ];

      if (this.exibirAcoes) {
        fields.push({
          key: "id",
          label: "",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-center",
        });
      }

      return fields;
    },
    qtdeTotal() {
      return this.items.reduce((total, item) => total + item.quantidade * 1, 0);
    },
    custoTotalSum() {
      return this.items.reduce((total, item) => total + this.totalCusto(item), 0);
    },
    tribTotalSum() {
      return this.items.reduce((total, item) => total + this.totalTrib(item), 0);
    },
    margemTotalSum() {
      return this.items.reduce((total, item) => total + this.margemTotal(item), 0);
    },
    comissaoTotalSum() {
      return this.items.reduce((total, item) => total + this.getComissao(item) * 1, 0);
    },
    valorTotalSum() {
      return this.items.reduce((total, item) => total + item.valor_total * 1, 0);
    },
  },
  methods: {
    compareSortValues(a, b) {
      const aEmpty = a === null || a === undefined || a === "";
      const bEmpty = b === null || b === undefined || b === "";

      if (aEmpty && bEmpty) {
        return 0;
      }
      if (aEmpty) {
        return -1;
      }
      if (bEmpty) {
        return 1;
      }

      const aNum = Number(a);
      const bNum = Number(b);
      const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);

      if (bothNumeric) {
        if (aNum < bNum) {
          return -1;
        }
        if (aNum > bNum) {
          return 1;
        }
        return 0;
      }

      return String(a).localeCompare(String(b), "pt-BR", {
        numeric: true,
        sensitivity: "base",
      });
    },
    sortHeaderClass(key) {
      if (this.localSortBy !== key) {
        return null;
      }
      return this.localSortDesc
        ? "orcamento-itens__group--sorted-desc"
        : "orcamento-itens__group--sorted-asc";
    },
    applySort(sortBy, sortDesc) {
      this.localSortBy = sortBy;
      this.localSortDesc = sortDesc;
      this.$emit("update:sortBy", sortBy);
      this.$emit("update:sortDirection", sortDesc ? "desc" : "asc");
      this.$emit("sort-changed", {
        sortBy,
        sortDesc,
      });
    },
    toggleSort(key) {
      const sortDesc = this.localSortBy === key ? !this.localSortDesc : false;
      this.applySort(key, sortDesc);
    },
    onTableSortChanged(event) {
      this.applySort(event.sortBy, !!event.sortDesc);
    },
    getComissao(item) {
      if (this.getComissaoFn) {
        return this.getComissaoFn(item) * 1;
      }
      return item.valor_comissao * 1 || 0;
    },
    ipiUnit(item) {
      return (item.valor_ipi * 1 || 0) + (item.valor_margem_ipi * 1 || 0);
    },
    stUnit(item) {
      return (item.valor_st * 1 || 0) + (item.valor_margem_st * 1 || 0);
    },
    totalCusto(item) {
      return (item.quantidade * 1 || 0) * (item.valor_unitario * 1 || 0);
    },
    totalTrib(item) {
      return (item.quantidade * 1 || 0) * (this.ipiUnit(item) + this.stUnit(item));
    },
    margemTotal(item) {
      return (item.quantidade * 1 || 0) * (item.margem_valor * 1 || 0);
    },
    premioUnit(item) {
      const qtde = item.quantidade * 1 || 0;
      if (!qtde) {
        return 0;
      }
      return this.getComissao(item) / qtde;
    },
  },
};
</script>

<style scoped>
.orcamento-itens {
  background: #fff;
  border: 1px solid #e4e7ea;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.orcamento-itens__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #edf0f2;
}

.orcamento-itens__title-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-left: 0.65rem;
  border-left: 3px solid #0075eb;
}

.orcamento-itens__title {
  font-size: 1rem;
  font-weight: 700;
  color: #23282c;
  line-height: 1.2;
}

.orcamento-itens__subtitle {
  font-size: 0.78rem;
  color: #8a94a6;
}

.orcamento-itens__header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.orcamento-itens__filial {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid #cfe2ff;
  background: #f3f8ff;
  color: #0075eb;
  font-size: 0.8rem;
  font-weight: 600;
}

.orcamento-itens__add-btn {
  font-weight: 600;
  padding: 0.45rem 0.95rem !important;
  border-radius: 8px !important;
  background-color: #7C3AED !important;
  border-color: #7C3AED !important;
}

.orcamento-itens__add-btn:hover:not(:disabled),
.orcamento-itens__add-btn:focus:not(:disabled) {
  background-color: #5804CA !important;
  border-color: #5804CA !important;
}

.orcamento-itens__body {
  overflow-x: auto;
}

.orcamento-itens__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 180px;
  color: #8a94a6;
  font-size: 1.1rem;
  font-weight: 600;
}

.orcamento-itens__table >>> table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.72rem;
}

.orcamento-itens__table >>> thead th {
  border-top: none;
  border-bottom: 1px solid #edf0f2;
  vertical-align: middle;
  white-space: nowrap;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.orcamento-itens__group {
  text-align: center !important;
  font-size: 0.68rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: none !important;
  padding: 0.45rem 0.5rem !important;
}

.orcamento-itens__group--solo {
  border-bottom: 1px solid #edf0f2 !important;
  vertical-align: middle !important;
}

.orcamento-itens__group--acoes {
  background: #fff !important;
}

.orcamento-itens__group--sortable {
  cursor: pointer;
  user-select: none;
}

.orcamento-itens__group--sortable:hover {
  filter: brightness(0.97);
}

.orcamento-itens__sort-icon {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 0.35rem;
  vertical-align: middle;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #adb5bd;
  opacity: 0.55;
}

.orcamento-itens__group--sorted-asc .orcamento-itens__sort-icon {
  border-top: 0;
  border-bottom: 5px solid currentColor;
  opacity: 1;
}

.orcamento-itens__group--sorted-desc .orcamento-itens__sort-icon {
  border-top-color: currentColor;
  opacity: 1;
}

.orcamento-itens__table >>> thead tr:nth-child(2) .orcamento-itens__th--spanned {
  display: none;
}

.orcamento-itens__group--custo {
  background-color: #EEF2F6 !important;
  color: #475569;
}

.orcamento-itens__group--custo > span {
  color: #191e25;
}

.orcamento-itens__table >>> .orcamento-itens__th--custo {
  background: #F8FAFC !important;
  color: #191e25;
  font-weight: 500;
}

.orcamento-itens__group--tributos {
  background-color: #FEF3C7 !important;
  color: #B85C14;
}

.orcamento-itens__table >>> .orcamento-itens__th--tributos {
  background: #FFF8E8 !important;
  color: rgb(180 83 9 / var(--tw-text-opacity, 1));
  font-weight: 500;
}

.orcamento-itens__group--margem {
  background-color: #D1FAE5 !important;
  color: #349778;
  font-weight: 500;
}

.orcamento-itens__table >>> .orcamento-itens__th--margem {
  background: #EAF8EF !important;
  color: rgb(4 120 87 / var(--tw-text-opacity, 1));
  font-weight: 500;
}

.orcamento-itens__group--premio {
  background-color: #E0F2FE !important;
  color: #1071A6;
  font-weight: 500;
}

.orcamento-itens__table >>> .orcamento-itens__th--premio {
  background: #EEF4FF !important;
  color: rgb(3 105 161 / var(--tw-text-opacity, 1));
  font-weight: 500;
}

.orcamento-itens__group--venda,
.orcamento-itens__table >>> .orcamento-itens__th--venda {
  background: rgb(186 230 253 / var(--tw-bg-opacity, 1)) !important;
  color: rgb(7 89 133 / var(--tw-text-opacity, 1)) !important;
}

.orcamento-itens__table >>> tbody {
  font-size: 0.72rem;
}

.orcamento-itens__table >>> tbody td {
  border-top: 1px solid #edf0f2;
  vertical-align: middle;
  padding: 0.55rem 0.5rem;
}

.orcamento-itens__table >>> tfoot th {
  /* background: #f8f9fa; */
  border-top: 1px solid #dee2e6;
  vertical-align: middle;
  padding: 0.65rem 0.5rem;
  font-weight: 700;
}

.orcamento-itens__table >>> tfoot .orcamento-itens__th--custo,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--tributos,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--margem,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--premio,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--venda {
  text-align: right !important;
}

.orcamento-itens__table >>> tfoot .orcamento-itens__th--spanned.text-center {
  text-align: center !important;
}

.orcamento-itens__table >>> tfoot .orcamento-itens__th--venda.orcamento-itens__th--spanned {
  text-align: right !important;
}

.orcamento-itens__produto {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 220px;
}

.orcamento-itens__codigo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: #e8eef5;
  color: rgb(38 56 213 / var(--tw-text-opacity, 1));
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

.orcamento-itens__nome {
  color: #23282c;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 1.25;
  word-break: break-word;
}

.orcamento-itens__num {
  font-weight: 700;
  color: #23282c;
}

.orcamento-itens__money {
  font-weight: 600;
  /* color: #23282c; */
  color: rgb(95 103 114);
  white-space: nowrap;
}

.orcamento-itens__money--custo-total {
  color: #191e25;
}

.orcamento-itens__money--trib {
  color: #e67e22;
}

.orcamento-itens__money--margem {
  color: #1b7a45;
}

.orcamento-itens__money--premio {
  color: #1071a6;
}

.orcamento-itens__money--venda {
  color: rgb(3 105 161 / var(--tw-text-opacity, 1));
  font-weight: 700;
}

.orcamento-itens__money--venda:before,
.orcamento-itens__table >>> tfoot .orcamento-itens__money:before {
  content: "R$ ";
}

.orcamento-itens__totais-label {
  font-weight: 700;
  text-transform: uppercase;
  color: #23282c;
}

.orcamento-itens__acoes {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.orcamento-itens__acao {
  width: 28px;
  height: 28px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px !important;
  background: #fff !important;
}

.orcamento-itens__acao--edit {
  border: 1px solid #cfe2ff !important;
  color: #0075eb !important;
}

.orcamento-itens__acao--delete {
  border: 1px solid #f5c2c7 !important;
  color: #dc3545 !important;
}

.orcamento-itens__note {
  padding: 0.55rem 1rem 0.75rem;
  color: #8a94a6;
  font-size: 0.72rem;
  border-top: 1px solid #edf0f2;
}

.orcamento-itens__spinner {
  display: inline-flex;
  color: #8a94a6;
}
</style>
